/**
 * Runtime guards for every record that crosses a boundary (sync, import, IndexedDB). Hand-
 * written so core stays dependency-free. Unknown extra keys are allowed: an older app must
 * accept a record a newer one wrote.
 */
import type {
  AvailabilityRating,
  AvailabilityTable,
  AvailabilityVote,
  BasketEntry,
  Category,
  Favourite,
  Food,
  FoodDraft,
  Ingredient,
  ItemRef,
  Match,
  Recipe,
  Region,
  Section,
  SyncRecord,
  Trip,
} from './types'

export type ParseResult<T> = { ok: true; value: T } | { ok: false; errors: string[] }

type Check = (v: unknown, path: string, errors: string[]) => void

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

const str: Check = (v, p, e) => {
  if (typeof v !== 'string') e.push(`${p}: expected a string`)
}
const nonEmpty: Check = (v, p, e) => {
  if (typeof v !== 'string' || !v.trim()) e.push(`${p}: expected a non-empty string`)
}
const num: Check = (v, p, e) => {
  if (typeof v !== 'number' || !Number.isFinite(v)) e.push(`${p}: expected a number`)
}
const nonNeg: Check = (v, p, e) => {
  if (typeof v !== 'number' || !Number.isFinite(v) || v < 0) {
    e.push(`${p}: expected a number ≥ 0`)
  }
}
const bool: Check = (v, p, e) => {
  if (typeof v !== 'boolean') e.push(`${p}: expected true or false`)
}
const oneOf =
  (...values: readonly (string | number)[]): Check =>
  (v, p, e) => {
    if (!values.includes(v as string | number)) e.push(`${p}: expected one of ${values.join(', ')}`)
  }
const nullable =
  (check: Check): Check =>
  (v, p, e) => {
    if (v !== null) check(v, p, e)
  }
const arr =
  (check: Check, min = 0): Check =>
  (v, p, e) => {
    if (!Array.isArray(v)) return void e.push(`${p}: expected a list`)
    if (v.length < min) e.push(`${p}: expected at least ${min}`)
    v.forEach((item, i) => check(item, `${p}[${i}]`, e))
  }
const rec =
  (check: Check): Check =>
  (v, p, e) => {
    if (!isObj(v)) return void e.push(`${p}: expected an object`)
    for (const [k, item] of Object.entries(v)) check(item, `${p}.${k}`, e)
  }
const obj =
  (required: Record<string, Check>, optional: Record<string, Check> = {}): Check =>
  (v, p, e) => {
    if (!isObj(v)) return void e.push(`${p}: expected an object`)
    for (const [k, check] of Object.entries(required)) {
      if (!(k in v) || v[k] === undefined) e.push(`${p}.${k}: missing`)
      else check(v[k], `${p}.${k}`, e)
    }
    for (const [k, check] of Object.entries(optional)) {
      if (v[k] !== undefined) check(v[k], `${p}.${k}`, e)
    }
  }
/** Passes when any alternative passes; reports the first alternative's errors otherwise. */
const either =
  (...checks: Check[]): Check =>
  (v, p, e) => {
    let first: string[] | undefined
    for (const c of checks) {
      const errs: string[] = []
      c(v, p, errs)
      if (!errs.length) return
      first ??= errs
    }
    e.push(...(first ?? []))
  }

const time: Check = (v, p, e) => {
  if (typeof v !== 'string' || !Number.isFinite(Date.parse(v))) e.push(`${p}: expected an ISO time`)
}
const money = obj({ amount: num, currency: nonEmpty })

const AISLES = [
  'produce',
  'bakery',
  'dairy',
  'meat',
  'pantry',
  'pasta-rice',
  'international',
  'breakfast',
  'snacks',
  'nuts-dried-fruit',
  'confectionery',
  'drinks',
  'spices-condiments',
  'health',
  'outdoor',
  'other',
] as const
const ROLES = ['breakfast', 'lunch', 'dinner', 'snack', 'drink', 'ingredient'] as const
const SOURCES = [
  'curated',
  'usda',
  'ausnut',
  'cofid',
  'openfoodfacts',
  'store',
  'panel-photo',
  'sheet-import',
  'manual',
] as const
const DIETS = ['vegetarian', 'vegan', 'gluten-free', 'dairy-free', 'nut-free', 'halal', 'kosher']
const SLOTS = ['breakfast', 'lunch', 'dinner'] as const

const NUTRITION = obj(
  {},
  {
    protein_g: nonNeg,
    fat_g: nonNeg,
    saturated_fat_g: nonNeg,
    carb_g: nonNeg,
    sugar_g: nonNeg,
    fibre_g: nonNeg,
    sodium_mg: nonNeg,
    potassium_mg: nonNeg,
  },
)
const PACK = obj({ size_g: nonNeg }, { serves: nullable(nonNeg), label: str })
const PRICE = obj(
  { amount: nonNeg, currency: nonEmpty, as_of: time },
  { per_g: nonNeg, store: str, region: str, on_special: bool },
)
const PREP = obj(
  { method: oneOf('none', 'cold-soak', 'just-add-boiling', 'simmer') },
  { water_ml_per_100g: nonNeg, minutes: nonNeg },
)

const RATING = obj(
  { level: oneOf(0, 1, 2, 3, 4, 5), source: oneOf('curated', 'user', 'community') },
  {
    price: oneOf('cheap', 'typical', 'pricey', 'very-pricey'),
    note: str,
    votes: nonNeg,
    updated_at: time,
  },
)

const REGION = obj(
  { code: nonEmpty, name: nonEmpty, currency: nonEmpty, label_energy: oneOf('kJ', 'kcal', 'both') },
  { trails: arr(str), shop_examples: arr(str) },
)

const CATEGORY = obj(
  { id: nonEmpty, name: nonEmpty, parent: nullable(nonEmpty) },
  {
    aliases: arr(str),
    aisle: oneOf(...AISLES),
    roles: arr(oneOf(...ROLES)),
    generic: nonEmpty,
    non_caloric: bool,
    description: str,
  },
)

const FOOD = obj(
  {
    id: nonEmpty,
    name: nonEmpty,
    kind: oneOf('generic', 'product'),
    category: nonEmpty,
    kcal_per_100g: nonNeg,
    source: oneOf(...SOURCES),
    verified: bool,
  },
  {
    brand: str,
    kj_per_100g: nonNeg,
    nutrition: NUTRITION,
    serve_g: nonNeg,
    serve_label: str,
    pack: PACK,
    prices: arr(PRICE),
    barcodes: arr(str),
    regions: arr(str),
    availability: rec(RATING),
    diet: arr(oneOf(...DIETS)),
    prep: PREP,
    shelf_stable: bool,
    source_ref: str,
    notes: str,
    updated_at: time,
    deleted: bool,
  },
)

const MATCH = either(obj({ food: nonEmpty }), obj({ category: nonEmpty }))
const ITEM_REF = either(
  obj({ food: nonEmpty }),
  obj({ recipe: nonEmpty }),
  obj({ category: nonEmpty }),
)

const INGREDIENT = obj(
  { id: nonEmpty, g: nonNeg, options: arr(MATCH, 1) },
  { label: str, default: nonNeg, non_caloric: bool, optional: bool, note: str },
)

const RECIPE = obj(
  {
    id: nonEmpty,
    name: nonEmpty,
    ingredients: arr(INGREDIENT),
    source: oneOf('curated', 'user', 'sheet-import'),
  },
  {
    meal: oneOf(...SLOTS, 'snack'),
    kcal_per_serve_override: nonNeg,
    method: str,
    prep: PREP,
    diet: arr(oneOf(...DIETS)),
    tags: arr(str),
    credit: str,
    notes: str,
    updated_at: time,
    deleted: bool,
  },
)

const PICKS = rec(MATCH)

const DAY_LINE = obj(
  { id: nonEmpty, item: ITEM_REF, serves_per_day: nonNeg },
  { picks: PICKS, note: str },
)
const MEAL_LINE = obj(
  { id: nonEmpty, slot: oneOf(...SLOTS), item: ITEM_REF },
  { count: nonNeg, picks: PICKS, note: str },
)

const BASKET_ENTRY = obj(
  {
    id: nonEmpty,
    food: nullable(str),
    label: str,
    units: nonNeg,
    kcal_each: nonNeg,
    grams_each: nonNeg,
    added_at: time,
    entry: oneOf('search', 'barcode', 'photo', 'quick', 'from-list'),
  },
  {
    category: str,
    price_each: money,
    macros_each: NUTRITION,
    for_line: str,
    meal: oneOf(...SLOTS, 'snack'),
  },
)

const SECTION = obj(
  {
    id: nonEmpty,
    name: str,
    days: nonNeg,
    method: oneOf('carry', 'food-drop', 'post', 'buy-in-town'),
    kcal_per_day: nonNeg,
    slots: obj({ breakfast: nonNeg, lunch: nonNeg, dinner: nonNeg }),
    day_lines: arr(DAY_LINE),
    meal_lines: arr(MEAL_LINE),
    status: oneOf('planned', 'shopping', 'shopped', 'walked'),
    basket: arr(BASKET_ENTRY),
  },
  {
    region: str,
    shop: str,
    buy_by: str,
    people: nonNeg,
    hours_per_day: nonNeg,
    not_here: arr(str),
    review: str,
    copied_from: str,
    notes: str,
    updated_at: time,
  },
)

const TRIP = obj(
  { id: nonEmpty, name: str, sections: arr(SECTION) },
  { region: str, notes: str, updated_at: time, deleted: bool },
)

const VOTE = obj(
  { category: nonEmpty, region: nonEmpty, level: oneOf(0, 1, 2, 3, 4, 5), updated_at: time },
  { food: str, price: oneOf('cheap', 'typical', 'pricey', 'very-pricey'), note: str },
)

const SYNC_RECORD = obj(
  {
    kind: oneOf('trip', 'food', 'recipe', 'availability', 'favourite'),
    id: nonEmpty,
    updated_at: time,
  },
  { deleted: bool },
)

const FAVOURITE = obj(
  { id: nonEmpty, target: oneOf('food', 'recipe'), ref: nonEmpty },
  { updated_at: time, deleted: bool },
)

const FOOD_DRAFT = obj(
  { name: nonEmpty, source: oneOf(...SOURCES) },
  {
    brand: str,
    category: str,
    category_confidence: nonNeg,
    kcal_per_100g: nonNeg,
    kj_per_100g: nonNeg,
    nutrition: NUTRITION,
    serve_g: nonNeg,
    kcal_per_serve: nonNeg,
    pack: PACK,
    price: PRICE,
    barcodes: arr(str),
    regions: arr(str),
    source_ref: str,
    issues: arr(str),
  },
)

function parser<T>(check: Check, name: string) {
  return (v: unknown): ParseResult<T> => {
    const errors: string[] = []
    check(v, name, errors)
    return errors.length ? { ok: false, errors } : { ok: true, value: v as T }
  }
}

export const parseRegion = parser<Region>(REGION, 'region')
export const parseAvailabilityRating = parser<AvailabilityRating>(RATING, 'rating')
export const parseAvailabilityTable = parser<AvailabilityTable>(rec(rec(RATING)), 'availability')
export const parseCategory = parser<Category>(CATEGORY, 'category')
export const parseFood = parser<Food>(FOOD, 'food')
export const parseMatch = parser<Match>(MATCH, 'match')
export const parseItemRef = parser<ItemRef>(ITEM_REF, 'item')
export const parseIngredient = parser<Ingredient>(INGREDIENT, 'ingredient')
export const parseRecipe = parser<Recipe>(RECIPE, 'recipe')
export const parseBasketEntry = parser<BasketEntry>(BASKET_ENTRY, 'entry')
export const parseSection = parser<Section>(SECTION, 'section')
export const parseTrip = parser<Trip>(TRIP, 'trip')
export const parseAvailabilityVote = parser<AvailabilityVote>(VOTE, 'vote')
export const parseSyncRecord = parser<SyncRecord>(SYNC_RECORD, 'record')
export const parseFavourite = parser<Favourite>(FAVOURITE, 'favourite')
export const parseFoodDraft = parser<FoodDraft>(FOOD_DRAFT, 'draft')

const guard =
  <T>(parse: (v: unknown) => ParseResult<T>) =>
  (v: unknown): v is T =>
    parse(v).ok

export const isRegion = guard(parseRegion)
export const isAvailabilityRating = guard(parseAvailabilityRating)
export const isCategory = guard(parseCategory)
export const isFood = guard(parseFood)
export const isMatch = guard(parseMatch)
export const isItemRef = guard(parseItemRef)
export const isIngredient = guard(parseIngredient)
export const isRecipe = guard(parseRecipe)
export const isBasketEntry = guard(parseBasketEntry)
export const isSection = guard(parseSection)
export const isTrip = guard(parseTrip)
export const isAvailabilityVote = guard(parseAvailabilityVote)
export const isSyncRecord = guard(parseSyncRecord)
export const isFavourite = guard(parseFavourite)
export const isFoodDraft = guard(parseFoodDraft)

/** Parse a list, keeping the good records and reporting the bad ones by index. */
export function parseMany<T>(
  values: unknown,
  parse: (v: unknown) => ParseResult<T>,
): { values: T[]; errors: { index: number; errors: string[] }[] } {
  const out: T[] = []
  const errors: { index: number; errors: string[] }[] = []
  if (!Array.isArray(values))
    return { values: out, errors: [{ index: -1, errors: ['expected a list'] }] }
  values.forEach((v, index) => {
    const r = parse(v)
    if (r.ok) out.push(r.value)
    else errors.push({ index, errors: r.errors })
  })
  return { values: out, errors }
}
